import React, {useEffect, useRef, useState} from 'react';
import {HiBell} from 'react-icons/hi';
import {IoClose} from 'react-icons/io5';
import './StackedNotificationToast.css';
import {NotificationDto} from "../../types/notification";
import { toRelativeTime } from '../../utils/timeUtils';

interface StackedNotificationToastProps {
  notification: NotificationDto;
  onClose: () => void;
  index: number;
  allNotifications?: NotificationDto[];
}

const StackedNotificationToast: React.FC<StackedNotificationToastProps> = ({
  notification,
  onClose,
  index,
  allNotifications = []
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [isExpandedExiting, setIsExpandedExiting] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>();
  const containerRef = useRef<HTMLDivElement>(null);

  // Group notifications by entity_type for this specific notification
  const currentTypeNotifications = allNotifications.filter(
    notif => notif.entity_type === notification.entity_type
  );
  const notificationCount = currentTypeNotifications.length;

  // Handle click outside to collapse expanded notifications
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (isExpanded) {
          // Start exit animation
          setIsExpandedExiting(true);
          setTimeout(() => {
            setIsExpanded(false);
            setIsExpandedExiting(false);
            // Restart timer after collapsing
            startTimer();
          }, 300);
        }
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) {
      startTimer();
    } else {
      stopTimer();
    }
  }, [isExpanded]);

  useEffect(() => {
    // Start timer when component mounts (for auto-dismiss)
    startTimer();
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  
  // Declare startTimer and stopTimer functions before they're used
  const startTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      handleClose();
    }, 10000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  const handleCardClick = () => {
    if (currentTypeNotifications.length > 1) {
      if (isExpanded) {
        // Start exit animation
        setIsExpandedExiting(true);
        setTimeout(() => {
          setIsExpanded(false);
          setIsExpandedExiting(false);
          // Restart timer after collapsing
          startTimer();
        }, 300);
      } else {
        setIsExpanded(true);
      }
    } else {
      // Single notification - redirect to notification page
      window.location.href = '/notification';
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onClose();
    }, 300);
  };

  const handleBellClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = '/notification';
  };

  const getMainToastStyles = () => {
    const baseStyles = "transform transition-all duration-300 ease-out";
    const visibilityStyles = isVisible 
      ? "translate-x-0 opacity-100 scale-100" 
      : "translate-x-full opacity-0 scale-95";
    
    const toastStyles = `
      bg-white dark:bg-gray-800 
      border border-gray-200 dark:border-gray-700 
      text-gray-700 dark:text-gray-200 
      shadow-lg rounded-xl
      backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95
      ${isExiting ? 'animate-slide-out' : 'animate-slide-in'}
    `;

    return `${baseStyles} ${visibilityStyles} ${toastStyles}`;
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-96"
    >
      {/* Stack effect backgrounds - only show visual stacking when not expanded */}
      {notificationCount > 1 && !isExpanded && !isExpandedExiting && (
        <>
          <div 
            className="absolute inset-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl"
            style={{
              transform: 'translateY(8px) translateX(-2px) scale(0.98)',
              zIndex: -2,
              opacity: 0.6
            }}
          />
          {notificationCount > 2 && (
            <div 
              className="absolute inset-0 bg-white  dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl"
              style={{
                transform: 'translateY(16px) translateX(-4px) scale(0.96)',
                zIndex: -3,
                opacity: 0.3
              }}
            />
          )}
        </>
      )}

      {/* Main notification */}
      <div 
        className={`${getMainToastStyles()} notification-main-card w-full cursor-pointer`}
        style={{
          animationDelay: `${index * 100}ms`,
          transform: `translateY(${index * 12}px)`,
          zIndex: 10
        }}
        onClick={handleCardClick}
      >
        <div className={`flex items-center p-4 rounded-xl w-full relative ${
          notification.success ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'
        }`}>
          {/* Icon and count */}
          <div 
            className="flex-shrink-0 mr-3 cursor-pointer relative"
            onClick={handleBellClick}
          >
            <div className="relative">
              <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <HiBell className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              </div>
              {notificationCount > 1 && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {notificationCount}
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-grow min-w-0 pr-8">
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {notification.short_description}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {toRelativeTime(notification.created_at)}
            </p>
          </div>

          {/* Close button - positioned absolutely */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1 rounded-full 
                     text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300
                     hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200
                     hover:scale-110 active:scale-95 z-10"
            aria-label="Close notification"
          >
            <IoClose className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded notifications - appear below the main card */}
      {(isExpanded || isExpandedExiting) && currentTypeNotifications.length > 1 && (
        <div className="mt-1 space-y-1 relative z-[9998] w-full">
          {currentTypeNotifications
            .slice(1, 6) // Skip the first one (main card) and show next 5 (backend already ordered)
            .map((notif, idx) => (
              <div
                key={notif.id}
                className={`
                  ${isExpandedExiting ? 'notification-expanded-card-exit' : 'notification-expanded-card'}
                  bg-white dark:bg-gray-800 
                  border border-gray-200 dark:border-gray-700 
                  rounded-lg shadow-md p-3 w-full
                  hover:shadow-lg hover:scale-[1.02] cursor-pointer
                  transition-all duration-200 ease-out
                  ${notif.success ? '!border-l-4 !border-l-green-500' : '!border-l-4 !border-l-red-500'}
                `}
                style={{
                  marginTop: `${idx === 0 ? 4 : 2}px`
                }}
                onClick={() => window.location.href = '/notification'}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-grow">
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                      {notif.short_description}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {toRelativeTime(notif.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          }
          {currentTypeNotifications.length > 6 && (
            <div 
              className={`text-xs text-gray-500 dark:text-gray-400 text-center py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 w-full
                         ${isExpandedExiting ? 'notification-expand-exit' : 'notification-expand-enter'}`}
              style={{ marginTop: '8px' }}
            >
              +{currentTypeNotifications.length - 6} more notifications
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StackedNotificationToast; 